import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Fade
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  MenuBook
} from '@mui/icons-material';

const JournalBook = ({ entries }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const bookRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && currentPage > 0) {
        handlePageTurn('prev');
      } else if (e.key === 'ArrowRight' && currentPage < entries.length - 1) {
        handlePageTurn('next');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, entries.length]);

  // Handle touch events for mobile
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;

    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;

    if (Math.abs(diff) > 50) { // Minimum swipe distance
      if (diff > 0 && currentPage < entries.length - 1) {
        handlePageTurn('next');
      } else if (diff < 0 && currentPage > 0) {
        handlePageTurn('prev');
      }
    }
    setTouchStart(null);
  };

  const handlePageTurn = (direction) => {
    if (isFlipping) return;
    setIsFlipping(true);

    setTimeout(() => {
      if (direction === 'next' && currentPage < entries.length - 1) {
        setCurrentPage(prev => prev + 1);
      } else if (direction === 'prev' && currentPage > 0) {
        setCurrentPage(prev => prev - 1);
      }
      setIsFlipping(false);
    }, 500); // Match animation duration
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <Box
      ref={bookRef}
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 800,
        height: { xs: '70vh', sm: '80vh' },
        mx: 'auto',
        perspective: '2000px',
        touchAction: 'none'
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Book Container */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          transformStyle: 'preserve-3d',
          transition: 'transform 0.5s ease-in-out',
          transform: isFlipping ? 'rotateY(180deg)' : 'rotateY(0)',
        }}
      >
        {/* Current Page */}
        <Fade in={!isFlipping} timeout={500}>
          <Paper
            elevation={8}
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backfaceVisibility: 'hidden',
              background: 'linear-gradient(45deg, #fff9f0 0%, #fff 100%)',
              borderRadius: 2,
              p: { xs: 3, sm: 6 },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              border: '1px solid #e0e0e0',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            }}
          >
            {/* Page Header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <MenuBook sx={{ fontSize: 40, color: '#457B9D', mb: 1 }} />
              <Typography variant="h5" sx={{ fontFamily: 'Playfair Display, serif', color: '#457B9D', fontWeight: 700 }}>
                {entries[currentPage]?.template}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {formatDate(entries[currentPage]?.date)}
              </Typography>
            </Box>

            {/* Page Content */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#90e0ef',
                  borderRadius: '4px',
                },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                  color: '#333',
                  fontFamily: 'Open Sans, sans-serif',
                }}
              >
                {entries[currentPage]?.text}
              </Typography>
            </Box>

            {/* Page Number */}
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                color: '#90e0ef',
                fontWeight: 600,
              }}
            >
              Page {currentPage + 1} of {entries.length}
            </Typography>
          </Paper>
        </Fade>

        {/* Navigation Buttons */}
        {!isMobile && (
          <>
            <IconButton
              onClick={() => handlePageTurn('prev')}
              disabled={currentPage === 0 || isFlipping}
              sx={{
                position: 'absolute',
                left: -60,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                boxShadow: 2,
              }}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              onClick={() => handlePageTurn('next')}
              disabled={currentPage === entries.length - 1 || isFlipping}
              sx={{
                position: 'absolute',
                right: -60,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                boxShadow: 2,
              }}
            >
              <ChevronRight />
            </IconButton>
          </>
        )}

        {/* Mobile Navigation Hints */}
        {isMobile && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 2,
              opacity: 0.7,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Swipe to turn pages
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default JournalBook; 